import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  formatDate,
  Identity,
  Money,
  SearchInput,
  SegmentedControl,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  VisuallyHidden,
} from '@hkdevs/design-system';
import { useChecks, type PayCheck } from '../../api/queries/pay';
import { useTeamReps, type TeamRep, type TeamView } from '../../api/queries/team';

/** My reps' URL state: History's list (by rep, the default, or every check) and the search. */
export interface MyRepsSearch {
  show?: 'checks' | undefined;
  q?: string | undefined;
}

export function parseMyRepsSearch(search: Record<string, unknown>): MyRepsSearch {
  return {
    show: search.show === 'checks' ? 'checks' : undefined,
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
  };
}

const SHOW = [
  { value: 'reps', label: 'By rep' },
  { value: 'checks', label: 'All checks' },
];

const dollars = (cents: number) => (cents / 100).toFixed(2);
const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** The reps whose name contains the query. */
export function filterReps(reps: readonly TeamRep[], query: string) {
  const q = query.trim().toLowerCase();
  return q ? reps.filter((rep) => rep.name.toLowerCase().includes(q)) : reps;
}

/** The team's checks whose rep or pay date ("01/06/2023") contains the query. */
export function filterTeamChecks(checks: readonly PayCheck[], query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return checks;
  return checks.filter(
    (check) =>
      (check.rep?.name ?? '').toLowerCase().includes(q) ||
      formatDate(check.payDate).toLowerCase().includes(q),
  );
}

export interface MyRepsViewProps {
  view: TeamView;
  search: MyRepsSearch;
  onSearchChange: (next: MyRepsSearch) => void;
  /** History: the rep's Pay History; pending: their pending pay. */
  onOpenRep: (repId: string) => void;
  onOpenCheck: (repId: string, payDate: string) => void;
}

/** A supervisor's My reps (pc-dbb.3): the reps, and on History the team's checks too. */
export function MyRepsView({
  view,
  search,
  onSearchChange,
  onOpenRep,
  onOpenCheck,
}: MyRepsViewProps) {
  const listChecks = view === 'history' && search.show === 'checks';
  const reps = useTeamReps(view);
  const checks = useChecks('all', listChecks);
  const query = search.q ?? '';
  const setQuery = (q: string) => onSearchChange({ ...search, q: q || undefined });
  const loading = reps.isPending || (listChecks && checks.isPending);
  const failed = reps.isError || (listChecks && checks.isError);

  if (failed) {
    return (
      <Alert
        tone="danger"
        title="Your reps couldn’t be loaded"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void reps.refetch();
              if (listChecks) void checks.refetch();
            }}
          >
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  }
  if (!loading && reps.data.length === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            title="No reps yet"
            description="Reps who report to you will appear here once they’re assigned."
            icon="users"
            headingLevel={2}
          />
        </CardBody>
      </Card>
    );
  }

  const shownReps = filterReps(reps.data ?? [], query);
  const shownChecks = filterTeamChecks((listChecks && checks.data?.items) || [], query);
  const count = listChecks
    ? plural(shownChecks.length, 'check', 'checks')
    : plural(shownReps.length, 'rep', 'reps');
  const clearSearch = (
    <Button size="sm" variant="secondary" onClick={() => setQuery('')}>
      Clear search
    </Button>
  );

  return (
    <Stack gap={3}>
      <Toolbar aria-label={listChecks ? 'Search checks' : 'Search reps'}>
        {view === 'history' && (
          <SegmentedControl
            aria-label="List"
            size="sm"
            options={SHOW}
            value={listChecks ? 'checks' : 'reps'}
            onChange={(value) =>
              onSearchChange({ ...search, show: value === 'checks' ? 'checks' : undefined })
            }
          />
        )}
        <ToolbarItem size="search">
          {listChecks ? (
            <SearchInput
              label="Search checks by rep or pay date"
              placeholder="Search by rep or pay date"
              value={query}
              onValueChange={setQuery}
            />
          ) : (
            <SearchInput
              label="Search reps by name"
              placeholder="Search by name"
              value={query}
              onValueChange={setQuery}
            />
          )}
        </ToolbarItem>
        <ToolbarSpacer />
        {!loading && <ToolbarCount>{count}</ToolbarCount>}
      </Toolbar>
      <Card flush>
        {loading ? (
          <Table aria-label="Reps" busy>
            <TableHead>
              <TableCell>Rep</TableCell>
              <TableCell>Pending</TableCell>
              <TableCell>Last paid</TableCell>
              <TableCell numeric>YTD earnings</TableCell>
            </TableHead>
            <TableBody>
              <TableLoading colSpan={4} rows={4} label="Loading your reps" />
            </TableBody>
          </Table>
        ) : listChecks ? (
          shownChecks.length > 0 ? (
            <Table aria-label="Team checks" responsive="cards">
              <TableHead>
                <TableCell>Rep</TableCell>
                <TableCell>Pay Date</TableCell>
                <TableCell numeric>Amount</TableCell>
                <TableCell width="44px">
                  <VisuallyHidden>Open</VisuallyHidden>
                </TableCell>
              </TableHead>
              <TableBody>
                {shownChecks.map((check) => (
                  <TableRow
                    key={`${check.rep?.id}/${check.payDate}`}
                    label={`Open ${check.rep?.name ?? ''}’s check for ${formatDate(check.payDate)}`}
                    onClick={() => check.rep && onOpenCheck(check.rep.id, check.payDate)}
                  >
                    <TableCell rowHeader tone="strong">
                      {check.rep?.name}
                    </TableCell>
                    <TableCell label="Pay Date" tone="strong">
                      {formatDate(check.payDate)}
                    </TableCell>
                    <TableCell label="Amount" numeric tone="strong">
                      <Money value={dollars(check.amountCents)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <CardBody>
              <EmptyState
                title="No matching checks"
                description="No checks match your search. Try a different rep or pay date."
                icon="search"
                headingLevel={2}
                actions={clearSearch}
              />
            </CardBody>
          )
        ) : shownReps.length > 0 ? (
          <Table aria-label="Reps" responsive="cards">
            <TableHead>
              <TableCell>Rep</TableCell>
              <TableCell>Pending</TableCell>
              <TableCell>Last paid</TableCell>
              <TableCell numeric>YTD earnings</TableCell>
              <TableCell width="44px">
                <VisuallyHidden>Open</VisuallyHidden>
              </TableCell>
            </TableHead>
            <TableBody>
              {shownReps.map((rep) => (
                <TableRow
                  key={rep.id}
                  label={
                    view === 'history'
                      ? `Open ${rep.name}’s pay history`
                      : `Open ${rep.name}’s pending pay`
                  }
                  onClick={() => onOpenRep(rep.id)}
                >
                  <TableCell rowHeader>
                    <Identity name={rep.name} size="sm" />
                  </TableCell>
                  <TableCell label="Pending">
                    {rep.pendingCount > 0 ? (
                      <Badge tone="warning">{rep.pendingCount} pending</Badge>
                    ) : (
                      <Badge tone="neutral">Up to date</Badge>
                    )}
                  </TableCell>
                  <TableCell label="Last paid" tone="muted">
                    {rep.lastPaidDate ? formatDate(rep.lastPaidDate) : 'Not yet paid'}
                  </TableCell>
                  <TableCell label="YTD earnings" numeric tone="strong">
                    <Money value={dollars(rep.ytdCents)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <CardBody>
            <EmptyState
              title="No matching reps"
              description="No reps match your search. Try a different name."
              icon="search"
              headingLevel={2}
              actions={clearSearch}
            />
          </CardBody>
        )}
      </Card>
    </Stack>
  );
}
